import qs from 'qs';
import { ref } from 'vue';
import type { LocationQuery, NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';

import { startSessionRecording, stopSessionRecording, track } from '@/helpers/analytics/mixpanel';
import { axios } from '@/helpers/axios';
import { leaveAuthBreadcrumb } from '@/helpers/bugsnag';
import {
  clearChunkErrorRedirect,
  handleChunkLoadError,
  isChunkLoadError,
  setChunkErrorRedirect,
} from '@/helpers/chunk-error-handler';
import AuthRoutes from '@/modules/auth/routes';
import Buffer from '@/modules/buffer/routes';
import BulkKeywordToolRoutes from '@/modules/bulk-keyword-tool/routes';
import CalendarRoutes from '@/modules/calendar/routes';
import categoryRoutes from '@/modules/category/routes';
import CompareListings from '@/modules/compare-listings/routes';
import CompetitorSalesRoutes from '@/modules/competitor-sales/routes';
import CompetitorRoutes from '@/modules/competitors/routes';
import DashboardRoutes from '@/modules/dashboard/routes';
import DeliveryStatusRoutes from '@/modules/delivery-status/routes';
import ExtensionRoutes from '@/modules/extension/routes';
import healthCheckRoutes from '@/modules/health-check/routes';
import KeywordLists from '@/modules/keyword-lists/routes';
import KeywordsRoutes from '@/modules/keywords/routes';
import ListingAuditRoutes from '@/modules/listing-audit/routes';
import ListingChangesRoutes from '@/modules/listing-changes/routes';
import ListingHelper from '@/modules/listing-helper/routes';
import Listings from '@/modules/listings/routes';
import OnboardingRoutes from '@/modules/onboarding/routes';
import PlansRoute from '@/modules/pricing/routes';
import pricingAffiliateRoutes from '@/modules/pricing_affiliate/routes';
import pricingAffiliateRedesignRoutes from '@/modules/pricing-affiliate-redesign/routes';
import RankCheckerRoutes from '@/modules/rank-checker/routes';
import SalesMapRoutes from '@/modules/sales-map/routes';
import SettingsRoutes from '@/modules/settings/routes';
import ShopInfoRoutes from '@/modules/shop-info/routes';
import ShopBuzzRoutes from '@/modules/shopbuzz/routes';
import ShortcutRoutes from '@/modules/shortcut/routes';
import spellCheckerRoutes from '@/modules/spell-checker/routes';
import SpottedOnEtsyRoutes from '@/modules/spotted-on-etsy/routes';
import SubscriptionThanks from '@/modules/subscription-thanks/routes';
import SurveyRoutes from '@/modules/survey/routes';
import tagRoutes from '@/modules/tags/routes';
import CategoryToolRoutes from '@/modules/tools/routes';
import TrafficStatsRoutes from '@/modules/traffic-stats/routes';
import TrendBuzzRoutes from '@/modules/trend-buzz/routes';
import trendingRoutes from '@/modules/trending/routes';
import UpgradeRoutes from '@/modules/upgrade/routes';
import VideoTool from '@/modules/video-tool/routes';

function parseQuery(query: string) {
  return qs.parse(query) as LocationQuery; // Cast the result to LocationQuery
}


declare module 'vue-router' {
  interface RouteMeta {
    metaTags?: Record<string, string>[]
    noScroll?: boolean
    requiresAuth?: boolean
    scrollGroup?: string
    startTime?: number
    title?: string
  }
}

const isProductionBuild = import.meta.env.VITE_APP_ENV === 'production';
const userLanguage = ref('');
const isAuthenticated = window.__DATA__?.user;

let routes = [];

routes = routes.concat(
  Buffer,
  BulkKeywordToolRoutes,
  CalendarRoutes,
  categoryRoutes,
  CategoryToolRoutes,
  CompareListings,
  CompetitorRoutes,
  CompetitorSalesRoutes,
  DashboardRoutes,
  DeliveryStatusRoutes,
  ExtensionRoutes,
  healthCheckRoutes,
  KeywordLists,
  KeywordsRoutes,
  ListingAuditRoutes,
  ListingChangesRoutes,
  ListingHelper,
  Listings,
  OnboardingRoutes,
  PlansRoute,
  pricingAffiliateRedesignRoutes,
  pricingAffiliateRoutes,
  RankCheckerRoutes,
  SalesMapRoutes,
  SettingsRoutes,
  ShopBuzzRoutes,
  ShopInfoRoutes,
  ShortcutRoutes,
  spellCheckerRoutes,
  SpottedOnEtsyRoutes,
  SubscriptionThanks,
  SurveyRoutes,
  tagRoutes,
  TrafficStatsRoutes,
  TrendBuzzRoutes,
  trendingRoutes,
  UpgradeRoutes,
  VideoTool,
);

if (!isAuthenticated) {
  routes = routes.concat(AuthRoutes);
}

if (!isProductionBuild) {
  routes = routes.concat();
}

routes = routes.concat([
  {
    path: '/lotti',
    name: 'lotti-redirect',
    beforeEnter: () => {
      router.push('/lotti');
    },
  },
  {
    path: '/maintenance', // This will match all routes that are not defined
    name: 'maintenance',
    component: () => import('@/modules/auth/pages/Maintenance.vue'),
  },
  {
    path: '/high-activity',
    name: 'error.too-many-requests',
    component: () => import('@/modules/auth/pages/TooManyRequests.vue'),
    meta: {
      title: 'eRank — Too Many Requests',
    },
  },
  {
    path: '/:pathMatch(.*)*', // This will match all routes that are not defined
    name: '404',
    component: () => import('@/modules/auth/pages/404.vue'),
  },
]);

const router = createRouter({
  history: createWebHistory(),
  linkActiveClass: 'active',
  parseQuery,
  routes,
  scrollBehavior: (to, from, savedPosition) => {
    // since vue-router does not allow a lot of customization,
    // we have to handle some paths of the scroll behavior ourselves.
    if (to.meta.noScroll) {
      // @ts-expect-error - Invalid types.
      if (to.href !== '/keyword-tool' && from.href !== '/keyword-tool') {
        return;
      }
    }

    // Preserve scroll when navigating between routes in the same group
    if (to.meta.scrollGroup && to.meta.scrollGroup === from.meta.scrollGroup) {
      return savedPosition;
    }

    if (to.hash) {
      return new Promise((resolve) => {
        const scrollToHash = () => {
          const element = document.querySelector(to.hash);
          if (element) {
            const offset = 80;
            const top = element.getBoundingClientRect().top + window.scrollY - offset;
            resolve({ top, behavior: 'smooth' });
          } else {
            resolve({ top: 0 });
          }
        };
        requestAnimationFrame(() => {
          requestAnimationFrame(scrollToHash);
        });
      });
    }

    return {
      top: 0,
    };
  },
  stringifyQuery: qs.stringify,
});

router.onError((error) => {
  const errorMessage = error?.message || String(error);

  if (isChunkLoadError(errorMessage)) {
    handleChunkLoadError();
    return;
  }

  console.error('Router error:', error);
});

router.beforeEach((to, from, next) => {
  setChunkErrorRedirect(to.fullPath);

  if (!to.meta.requiresAuth) {
    // For routes that don't require auth, just check window.__DATA__
    const isAuthenticated = window.__DATA__?.user;
    if (isAuthenticated && (to.path === '/login' || to.path === '/registration')) {
      // Redirect authenticated users from login/registration to dashboard
      next({ path: '/dashboard' });
      return;
    }
    processRoute(to, next);
    return;
  }

  axios.get('/account/member-preferences')
    .then(() => {
      handleAuthenticatedUser(to, from, next);
    })
    .catch((error) => {
      if (error.response?.status === 401) {
        // User is not authenticated - redirect to login
        handleUnauthenticatedUser(to, next);
      } else {
        const isAuthenticated = window.__DATA__?.user;
        if (to.meta.requiresAuth && !isAuthenticated) {
          handleUnauthenticatedUser(to, next);
        } else {
          handleAuthenticatedUser(to, from, next);
        }
      }
    });
});

function handleAuthenticatedUser(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
  const isAuthenticated = window.__DATA__?.user;

  // Redirect authenticated users from login/registration to dashboard
  if (isAuthenticated && (to.path === '/login' || to.path === '/registration')) {
    // redirect to query location if it exists
    if (to.query.location) {
      const location =
        typeof to.query.location === 'string'
          ? to.query.location
          : to.query.location[0];

      if (location) {
        const query = { ...to.query };
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete query.location;
        next({
          path: location,
          query,
        });
        return;
      }
    } else {
      next({
        path: '/dashboard',
        query: to.query,
      });
      return;
    }
  }

  processRoute(to, next);
}

function handleUnauthenticatedUser(to: RouteLocationNormalized, next: NavigationGuardNext) {
  if (!to.fullPath.includes('/login')) {
    // Merge existing query params with redirect
    next({
      name: 'login.entry',
      query: {
        ...to.query,
        location: to.fullPath,
      },
    });
  } else {
    next({
      name: 'login.entry',
    });
  }
}

function processRoute(to: RouteLocationNormalized, next: NavigationGuardNext) {
  // eslint-disable-next-line no-param-reassign
  to.meta.startTime = performance.now();

  // MetaTag route handler
  if (to.meta.title) {
    document.title = to.meta.title;
  } else {
    document.title = 'eRank';
  }

  Array.from(document.querySelectorAll('[data-vue-router-controlled]')).forEach(
    el => el.parentNode.removeChild(el),
  );

  if (to.meta.metaTags) {
    to.meta.metaTags.forEach(tag => {
      const metaTag = document.createElement('meta');

      Object.keys(tag).forEach(key => {
        metaTag.setAttribute(key, tag[key]);
      });

      metaTag.setAttribute('data-vue-router-controlled', '');
      document.head.appendChild(metaTag);
    });
  }

  const isAuthenticated = window.__DATA__?.user;
  if (to.meta.requiresAuth && !isAuthenticated) {
    handleUnauthenticatedUser(to, next);
  } else {
    next();
  }
}

router.afterEach(() => {
  clearChunkErrorRedirect();
});

function isAuthRoute(routeName: unknown): routeName is string {
  if (!routeName) {
    return false;
  }

  return ['login', 'registration'].some((prefix) => String(routeName).startsWith(prefix));
}

router.afterEach((to, from) => {
  if (!isAuthRoute(to.name)) {
    return;
  }

  leaveAuthBreadcrumb('Navigated to auth route', {
    fromRoute: from?.name ? String(from.name) : undefined,
    routeName: String(to.name),
  });
});

router.afterEach((to, from) => {
  userLanguage.value = navigator.language || navigator.languages[0];
  const isAuthenticated = window.__DATA__?.user;
  const routeName = to.matched[0].name;
  const allowedRoutes = ['plans', 'keyword-research', 'competitor-sales', 'dashboard', 'listing-audit', 'login', 'registration', '404'];
  const enableReplay = allowedRoutes.includes(routeName as string);

  if (enableReplay) {
    startSessionRecording();
  } else {
    stopSessionRecording();
  }

  if (to.meta.title) {
    track('Viewed Page', {
      'Page Title': to.meta.title,
      'Browser Language': userLanguage.value,
      'Logged In Status': isAuthenticated ? 'TRUE' : 'FALSE',
      'Erank Version': 3,
    });
  }

  if (!isAuthenticated) {
    if (from.path !== to.path) {
      const { startTime } = to.meta;

      axios.post('public/logs', {
        action: 'v3.pageview',
        value: JSON.stringify({
          date: Date.now(),
          load_time: startTime
            ? (performance.now() - (startTime as number)) / 1000
            : undefined,
          path: to.path,
          query: to.query,
        }),
      });
    }

    return;
  }

  if (from.path !== to.path) {
    const { startTime } = to.meta;

    axios.post('logs', {
      action: 'v3.pageview',
      value: JSON.stringify({
        date: Date.now(),
        load_time: startTime
          ? (performance.now() - (startTime as number)) / 1000
          : undefined,
        path: to.path,
        query: to.query,
      }),
    });
  }
});

export default router;
